import { Component, forwardRef, Input, OnDestroy, ViewChild } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormsModule, NG_VALIDATORS, NG_VALUE_ACCESSOR, NgForm, Validator } from '@angular/forms';
import { Subscription } from 'rxjs';
import { FieldMetaData, GrundstueckNummerSearchRequestMeta } from '../../../core/gb-api';
import { GbGemeindeSearchMode } from '../gb-gemeinde/gbGemeindeSearchMode';
import { GbGemeindeComponent } from '../gb-gemeinde/gb-gemeinde.component';
import { RegionenComponent } from '../regionen/regionen.component';
import { keysToTranslate } from 'i18n/keys-to-translate';
import { CapFormRowDirective } from '../../form-control/cap-form-row.directive';
import { CapFormControlComponent } from '../../form-control/form-control.component';
import { MetaDisablerDirective } from '../../directives/meta-disabler.directive';
import { MetaValidatorDirective } from '../../directives/meta-validator.directive';
import { PermissionDirective } from '../../directives/permission.directive';
import { NgClass } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';

function isVisible(field: FieldMetaData): boolean {
  return field?.access !== 'NONE';
}

@Component({
  selector: 'cap-grundstueck-nummer',
  templateUrl: './grundstueck-nummer.component.html',
  styleUrls: ['./grundstueck-nummer.component.scss'],
  providers: [
    { provide: NG_VALIDATORS, useExisting: forwardRef(() => GrundstueckNummerComponent), multi: true },
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => GrundstueckNummerComponent), multi: true }
  ],
  imports: [
    FormsModule,
    CapFormRowDirective,
    CapFormControlComponent,
    RegionenComponent,
    MetaDisablerDirective,
    MetaValidatorDirective,
    GbGemeindeComponent,
    PermissionDirective,
    NgClass,
    TranslatePipe
  ]
})
export class GrundstueckNummerComponent implements OnDestroy, ControlValueAccessor, Validator {
  @Input()
  searchMode: GbGemeindeSearchMode;

  @Input()
  mitRegion = false;

  @Input()
  hasfocus = false;

  @ViewChild(NgForm, { static: false })
  form: NgForm;

  @ViewChild(GbGemeindeComponent, { static: true })
  private gemeindeComponent: GbGemeindeComponent;

  @ViewChild(RegionenComponent, { static: false })
  private regionenComponent: RegionenComponent;

  @Input()
  name: string;

  @Input()
  heading: string;

  @Input() nurGueltige = true;

  constructor() {
    keysToTranslate<string>(['Index1-2', 'Index1-3']);
  }

  model: GrundstueckNummerSearchRequestMeta;
  indexPrefix = 'Index';
  indexTitle = 'Index';

  index1Field: string;
  index2Field: string;

  private _subscription: Subscription;

  onTouched: () => void;
  onValidatorChange: () => void;

  registerOnValidatorChange?(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  writeValue(obj: any): void {
    this.model = obj;
    this.formatIndex();
  }

  formatIndex(): void {
    if (!this.model) {
      return;
    }
    if (!isVisible(this.model.mNummer4) && !isVisible(this.model.mNummer3)) {
      this.index1Field = 'index-last';
    } else if (!isVisible(this.model.mNummer4)) {
      this.indexTitle = this.indexPrefix + '1-2';
      this.index1Field = 'index-middle';
      this.index2Field = 'index-last';
    } else {
      this.indexTitle = this.indexPrefix + '1-3';
      this.index1Field = 'index-middle';
      this.index2Field = 'index-middle';
    }
  }

  emptyGemeindeSearch() {
    this.gemeindeComponent.onClear();
    if (this.model == null) {
      return;
    }
    this.model.gbGemeindeId = null;
  }

  emptyRegionSearch() {
    this.regionenComponent.onClear();
    if (this.model == null) {
      return;
    }
    this.model.region = null;
  }

  registerOnChange(fn: any): void {
    this._subscription = this.form.control.valueChanges.subscribe(() => {
      // we can't pass the inner form's model because that model is derived from the form model
      // which does not know about, and therefore copy, properties not mapped with ngModel, such as our metas
      // to preserve the metas, we therefore pass our original model. Hopefully this never causes a problem ... (MOO)
      fn(this.model);
    });
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
    // TODO das formular fragen, wann der Event auftritt, damit wir nicht unser Template verunstalten muessen
  }

  public focus() {
    this.gemeindeComponent.focus();
  }

  processUniqueNummer(nummer: string): string {
    const uniqueValues = Array.from(new Set(nummer.split(/\s+/)));
    return uniqueValues.join(' ');
  }

  validate(c: AbstractControl): {
    [key: string]: any;
  } | null {
    return this.form.valid ? null : { nestedErrors: true };
    // we can't return {} to signal nested errors in a semantically appropriate way
    // because mergeErrors helpfully ignores objects without keys :-(
  }

  ngOnDestroy(): void {
    this._subscription?.unsubscribe();
  }
}
